import type { FC, SetStateAction } from "react";
import React, { useContext, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";

import { useUpdateMe } from "@/api/me";
import FieldInput from "@/components/form/inside-label-field-input";
import DialogCustom from "@/components/modals/dialog-custom";
import UpdatePasswordModal from "@/components/modals/profile/update-password-modal";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AuthContext } from "@/contexts/auth-context";
import type { UserUpdateFormData } from "@/schemas/user";
import { updateUserSchema } from "@/schemas/user";
import { resetForm, stopPropagation } from "@/utils/form";

interface AccountSideModalProps {
  open: boolean;
  onOpenChange: React.Dispatch<SetStateAction<boolean>>;
}

const AccountSideModal: FC<AccountSideModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { user } = useContext(AuthContext);
  const { mutateAsync, isPending } = useUpdateMe();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      lastName: user?.lastName ?? "",
      firstName: user?.firstName ?? "",
      email: user?.email ?? "",
    } as UserUpdateFormData,
    onSubmit: async ({ value, formApi }) => {
      const values = updateUserSchema.parse(value);
      await mutateAsync(values, {
        onSuccess: () => {
          onOpenChange(false);
          resetForm(values, formApi);
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.code === "ERR_NETWORK")
              return {
                fields: {
                  comment: "Erreur réseau, merci de réessayer ultérieurement",
                },
              };
          }
        },
      });
    },
  });

  return (
    <>
      <DialogCustom
        title="Informations générales"
        description={
          <div className="text-sm text-gray-600">
            {"Gestion de mes informations"}
          </div>
        }
        open={open}
        onOpenChange={onOpenChange}
        side
      >
        <Separator />
        <div className="flex h-full flex-col gap-5 p-8 bg-grey-25">
          <form
            onSubmit={(e) => stopPropagation(e, handleSubmit)}
            className="grid grid-flow-row grid-cols-1 gap-6 px-6 w-full"
          >
            <FieldInput
              Field={Field}
              label={"Nom de famille"}
              name="lastName"
              placeholder="Nom de famille"
              schema={updateUserSchema}
            />
            <FieldInput
              Field={Field}
              label={"Prénom"}
              name="firstName"
              placeholder="Prénom"
              schema={updateUserSchema}
            />
            <FieldInput
              Field={Field}
              label={"Email"}
              name="email"
              placeholder="Email"
              schema={updateUserSchema}
            />
            <input type="submit" className="hidden" />
          </form>
        </div>
        <Separator />
        <div className="flex flex-col pt-6 px-6 gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleSubmit}
            loading={isPending}
          >
            Confirmer
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setIsModalOpen(true)}
          >
            Modifier mon mot de passe
          </Button>
        </div>
      </DialogCustom>
      <UpdatePasswordModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  );
};

export default AccountSideModal;
